import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlacesAutocomplete, type PlaceResult } from '../../hooks/usePlacesAutocomplete';

interface LandmarkSearchProps {
  onSelect: (place: PlaceResult | null) => void;
  selectedPlace?: PlaceResult | null;
  placeholder?: string;
  className?: string;
}

const LANDMARK_TYPES = [
  'school',
  'university',
  'hospital',
  'shopping_mall',
  'transit_station',
  'subway_station',
  'park',
  'mosque',
  'restaurant',
  'gym',
];

export default function LandmarkSearch({
  onSelect,
  selectedPlace: externalSelectedPlace,
  placeholder,
  className = '',
}: LandmarkSearchProps) {
  const { t, i18n } = useTranslation(['search', 'common']);
  const direction = i18n.dir();
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [recentSearches, setRecentSearches] = useState<PlaceResult[]>([]);

  const {
    query,
    setQuery,
    suggestions,
    isLoading,
    isReady,
    selectedPlace: internalSelectedPlace,
    selectPlace,
    clearSelection,
  } = usePlacesAutocomplete({
    types: LANDMARK_TYPES,
    componentRestrictions: { country: 'sa' },
  });

  const selectedPlace = externalSelectedPlace ?? internalSelectedPlace;

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentLandmarks');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved).slice(0, 5));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  // Save to recent searches
  const saveToRecent = useCallback((place: PlaceResult) => {
    const updated = [place, ...recentSearches.filter((p) => p.placeId !== place.placeId)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentLandmarks', JSON.stringify(updated));
  }, [recentSearches]);

  // Handle place selection
  const handleSelect = useCallback(
    async (place: PlaceResult) => {
      await selectPlace(place);
      saveToRecent(place);
      onSelect(place);
      setIsFocused(false);
    },
    [selectPlace, saveToRecent, onSelect]
  );

  // Handle clear
  const handleClear = useCallback(() => {
    clearSelection();
    onSelect(null);
    inputRef.current?.focus();
  }, [clearSelection, onSelect]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get icon for place type
  const getPlaceIcon = (types: string[]) => {
    if (types.includes('school') || types.includes('university')) return 'ri-school-line';
    if (types.includes('hospital') || types.includes('doctor')) return 'ri-hospital-line';
    if (types.includes('shopping_mall') || types.includes('store')) return 'ri-store-2-line';
    if (types.includes('transit_station') || types.includes('subway_station')) return 'ri-train-line';
    if (types.includes('park')) return 'ri-tree-line';
    if (types.includes('mosque')) return 'ri-building-2-line';
    if (types.includes('restaurant')) return 'ri-restaurant-line';
    if (types.includes('gym')) return 'ri-run-line';
    return 'ri-map-pin-line';
  };

  const showDropdown = isFocused && (suggestions.length > 0 || isLoading || (!query && recentSearches.length > 0));

  return (
    <div className={`relative ${className}`} dir={direction}>
      {/* Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <i className={`${selectedPlace ? 'ri-map-pin-fill text-[var(--pv-color-primary)]' : 'ri-search-line text-gray-400'}`}></i>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={selectedPlace ? selectedPlace.name : query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          placeholder={placeholder || t('search.nearYou.searchPlaceholder', { defaultValue: 'Search for a school, mall, hospital...' })}
          className={[
            'w-full pl-10 pr-10 py-2.5 rounded-lg border text-sm transition-colors',
            'bg-white dark:bg-[var(--pv-color-surface-soft)]',
            'text-gray-900 dark:text-[var(--pv-color-text)]',
            'placeholder-gray-400 dark:placeholder-[var(--pv-color-text-muted)]',
            isFocused
              ? 'border-[var(--pv-color-primary)] ring-2 ring-[var(--pv-color-primary)]/20'
              : 'border-gray-200 dark:border-[var(--pv-border-soft)]',
          ].join(' ')}
          disabled={!isReady}
        />
        {/* Clear button */}
        {(selectedPlace || query) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
          >
            <i className="ri-close-line"></i>
          </button>
        )}
        {/* Loading indicator */}
        {isLoading && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <i className="ri-loader-4-line animate-spin text-gray-400"></i>
          </div>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 py-2 bg-white dark:bg-[var(--pv-color-surface-strong)] rounded-lg border border-gray-200 dark:border-[var(--pv-border-soft)] shadow-lg max-h-64 overflow-y-auto"
        >
          {/* Recent searches */}
          {!query && recentSearches.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-[var(--pv-color-text-muted)]">
                {t('search.nearYou.recentLandmarks', { defaultValue: 'Recent searches' })}
              </div>
              {recentSearches.map((place) => (
                <button
                  key={place.placeId}
                  type="button"
                  onClick={() => handleSelect(place)}
                  className="w-full px-3 py-2 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors text-left"
                >
                  <i className={`${getPlaceIcon(place.types)} text-lg text-gray-400 mt-0.5`}></i>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-[var(--pv-color-text)] truncate">
                      {place.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-[var(--pv-color-text-muted)] truncate">
                      {place.address}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {/* Search results */}
          {query && suggestions.length > 0 && (
            suggestions.map((place) => (
              <button
                key={place.placeId}
                type="button"
                onClick={() => handleSelect(place)}
                className="w-full px-3 py-2 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-[var(--pv-color-surface-soft)] transition-colors text-left"
              >
                <i className={`${getPlaceIcon(place.types)} text-lg text-[var(--pv-color-primary)] mt-0.5`}></i>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-[var(--pv-color-text)] truncate">
                    {place.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-[var(--pv-color-text-muted)] truncate">
                    {place.address}
                  </div>
                </div>
              </button>
            ))
          )}

          {/* Loading */}
          {query && isLoading && (
            <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-[var(--pv-color-text-muted)]">
              <i className="ri-loader-4-line animate-spin text-[var(--pv-color-primary)] text-lg"></i>
              <p className="mt-1">{t('search.nearYou.searching', { defaultValue: 'Searching...' })}</p>
            </div>
          )}

          {/* No results */}
          {query && suggestions.length === 0 && !isLoading && (
            <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-[var(--pv-color-text-muted)]">
              {t('search.nearYou.noResults', { defaultValue: 'No landmarks found' })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
